/**
 * Manages dark/light mode using next-themes.
 *
 * - Persists user preference to localStorage
 * - Respects `enable_darkmode` feature flag
 * - If flag is disabled, forces light mode and updates localStorage
 * - Flags are checked once on mount (loaded via `/wafflejs` script)
 */

"use client";

import { ThemeProvider } from "next-themes";
import type { ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { useWaffle } from "@shared/contexts/WaffleContext/WaffleContext";

export type ColorModeProviderProps = {
  children: ReactNode;
  /** Force a specific color mode (user cannot toggle) */
  forcedMode?: "light" | "dark";
  /** Default color mode when no preference is set */
  defaultMode?: "light" | "dark";
  /** localStorage key for user preference */
  storageKey?: string;
  /**
   * CSP nonce for inline script injection.
   * Required for Content Security Policy compliance.
   */
  nonce?: string;
};

export function ColorModeProvider({
  children,
  forcedMode,
  defaultMode = "light",
  storageKey = "showpass-color-mode",
  nonce,
}: ColorModeProviderProps) {
  const waffle = useWaffle();

  const effectiveForcedMode = useMemo(() => {
    if (forcedMode) {
      return forcedMode;
    }

    const waffleLoaded = Object.keys(waffle.FLAGS).length > 0;
    if (!waffleLoaded) {
      return undefined;
    }

    const isDarkModeEnabled = waffle.isFlagActive("enable_darkmode");
    return isDarkModeEnabled ? undefined : "light";
  }, [forcedMode, waffle]);

  useEffect(() => {
    if (effectiveForcedMode === "light") {
      try {
        localStorage.setItem(storageKey, "light");
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }, [effectiveForcedMode, storageKey]);

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme={defaultMode}
      forcedTheme={effectiveForcedMode}
      storageKey={storageKey}
      disableTransitionOnChange // Prevents flash during theme switch
      enableSystem={false} // Don't follow OS preference (explicit user choice only)
      nonce={nonce}
    >
      {children}
    </ThemeProvider>
  );
}
